/**
 * Landing content model (redesign brief §5, content file `anonymoushelp-content-el.json`).
 *
 * WHY THIS SHAPE
 * The first CMS pass stored one flat `key -> string` row per field, which cannot
 * express the repeaters this content needs (4 barrier cards, 6 guarantees, 11 FAQ
 * items, reorderable). Rather than replace the CMS, each SECTION is stored as one
 * content block whose body is JSON. That keeps the existing draft/publish pipeline,
 * the audit log and the public read endpoint untouched, and gives the admin editor
 * real add/remove/reorder.
 *
 * Reading: `cms[section]` is a JSON string; parse it, fall back to the default
 * below on any failure. A marketing page must never show an error wall, and a
 * half-typed draft must never blank the site.
 *
 * Keys beginning with `_` in the source JSON are author notes. They are stripped
 * here deliberately — they must never reach the page.
 *
 * TEXT IS LOCKED. The Greek copy below is final and owner-verified. Do not edit it
 * to fit a layout; change the layout.
 */

/* LANDING_NAMESPACE ('landing') is re-used from ./landing — same CMS namespace,
   new per-section block layout. Importing rather than redeclaring keeps a single
   source of truth and avoids a duplicate export from the package index. */

/** Every section is one CMS block. Order here is the render order on the page. */
export const LANDING_SECTIONS = [
  'global',
  'theme',
  'crisis_bar',
  'hero',
  'barriers',
  'how_it_works',
  'anonymity',
  'psychologists',
  'pricing',
  'first_session',
  'faq',
  'therapist_cta',
  'final_cta',
  'footer',
  'seo',
] as const;

export type LandingSection = (typeof LANDING_SECTIONS)[number];

// ── Types ──────────────────────────────────────────────────────────────

export interface ImageField {
  src: string;
  /** Mandatory before save (brief §5 rule 2) — an empty alt fails validation. */
  alt: string;
}

export interface GlobalContent {
  brand_name: string;
  tagline: string;
  logo_url: string;
  nav: {
    how_it_works: string;
    psychologists: string;
    anonymity: string;
    pricing: string;
    faq: string;
    sign_in: string;
    cta_primary: string;
  };
  cta: { primary: string; secondary: string; browse: string; therapist: string };
}

/**
 * Brand colours the owner can change without a redeploy.
 *
 * Deliberately a short list. Every value here is paired in the editor with the
 * surface it actually sits on, so the contrast ratio shown is the real one the
 * visitor gets — a picker that reports a colour in isolation tells the owner
 * nothing about whether the text will be readable.
 *
 * The crisis bar's own colours are NOT here. They are fixed in the stylesheet:
 * the one element that must stay legible is the one nobody gets to restyle.
 */
export interface ThemeContent {
  primary: string;
  primary_ink: string;
  deep: string;
  accent: string;
  accent_soft: string;
}

export interface CrisisBarContent {
  /**
   * Ethically mandatory (brief §1, §5 rule 1). The dashboard renders this as a
   * disabled control and the server rejects `false` — the bar cannot be turned
   * off and the numbers cannot be emptied.
   */
  enabled_locked: true;
  text: string;
  link_label: string;
  lines: { label: string; value: string }[];
}

export interface HeroContent {
  eyebrow: string;
  h1: string;
  subhead: string;
  cta_primary: string;
  cta_secondary: string;
  cta_microcopy: string;
  image: ImageField;
  trust_badges: { icon: string; text: string }[];
}

export interface BarriersContent {
  h2: string;
  intro: string;
  items: { objection: string; answer: string; icon: string }[];
}

export interface HowItWorksContent {
  h2: string;
  subhead: string;
  steps: { number: string; title: string; text: string; meta: string; image: ImageField }[];
}

export interface AnonymityContent {
  h2: string;
  subhead: string;
  guarantees: { text: string }[];
  honest_limit_title: string;
  honest_limit_text: string;
  honest_limit_text_2: string;
  link_label: string;
  link_url: string;
}

export interface PsychologistsContent {
  h2: string;
  subhead: string;
  filter_label: string;
  categories: { key: string; label: string }[];
  card_labels: {
    speaks: string;
    session_price: string;
    session_duration: string;
    watch_intro: string;
    view_profile: string;
    book: string;
  };
  /** Licensed psychologists vs counsellors/coaching — kept visibly separate (brief §4.1). */
  type_labels: { psychologist: string; counselor: string; tab_psychologists: string; tab_counselors: string };
  empty_state_title: string;
  empty_state_text: string;
  empty_state_cta: string;
}

export interface PricingContent {
  h2: string;
  text: string;
  points: { title: string; text: string }[];
}

export interface FirstSessionContent {
  h2: string;
  text: string;
  highlight: string;
  image: ImageField;
}

export interface FaqContent {
  h2: string;
  subhead: string;
  items: { q: string; a: string }[];
}

export interface TherapistCtaContent {
  h2: string;
  text: string;
  cta: string;
  cta_url: string;
}

export interface FinalCtaContent {
  h2: string;
  text: string;
  cta_primary: string;
  cta_microcopy: string;
  image: ImageField;
}

export interface FooterContent {
  about_title: string;
  about_text: string;
  contact_title: string;
  phone_gr_label: string;
  phone_gr: string;
  phone_cy_label: string;
  phone_cy: string;
  email: string;
  /** Carried over from the previous landing — empty url hides the icon. */
  social: { facebook_url: string; instagram_url: string };
  disclaimer_title: string;
  disclaimer: string;
  links: { label: string; url: string }[];
  copyright: string;
}

export interface SeoContent {
  title: string;
  meta_description: string;
  og_title: string;
  og_description: string;
  og_image: string;
}

export interface LandingContent {
  global: GlobalContent;
  theme: ThemeContent;
  crisis_bar: CrisisBarContent;
  hero: HeroContent;
  barriers: BarriersContent;
  how_it_works: HowItWorksContent;
  anonymity: AnonymityContent;
  psychologists: PsychologistsContent;
  pricing: PricingContent;
  first_session: FirstSessionContent;
  faq: FaqContent;
  therapist_cta: TherapistCtaContent;
  final_cta: FinalCtaContent;
  footer: FooterContent;
  seo: SeoContent;
}

// ── Defaults (el) ──────────────────────────────────────────────────────

export const LANDING_CONTENT_EL: LandingContent = {
  global: {
    brand_name: 'Anonymous Help',
    tagline: 'Ανώνυμη ψυχολογική υποστήριξη',
    logo_url: '',
    nav: {
      how_it_works: 'Πώς λειτουργεί',
      psychologists: 'Οι ψυχολόγοι',
      anonymity: 'Η ανωνυμία σου',
      pricing: 'Κόστος',
      faq: 'Συχνές ερωτήσεις',
      sign_in: 'Σύνδεση',
      cta_primary: 'Ξεκίνα ανώνυμα',
    },
    cta: {
      primary: 'Ξεκίνα ανώνυμα',
      secondary: 'Έχω ήδη λογαριασμό',
      browse: 'Δες τους ψυχολόγους',
      therapist: 'Δωρεάν εγγραφή θεραπευτή',
    },
  },

  // Defaults mirror tokens.css. Changing them here changes the live site; the
  // stylesheet stays the fallback for a visitor whose CMS fetch fails.
  theme: {
    primary: '#1e8a7a',
    primary_ink: '#14675a',
    deep: '#133a38',
    accent: '#e8a13c',
    accent_soft: '#fbf0dc',
  },

  crisis_bar: {
    enabled_locked: true,
    text: 'Αν βρίσκεσαι σε κρίση αυτή τη στιγμή, μη χρησιμοποιήσεις την πλατφόρμα — κάλεσε 1018 ή 112.',
    link_label: 'Γραμμές άμεσης βοήθειας',
    lines: [
      { label: '1018 — Γραμμή Παρέμβασης για την Αυτοκτονία', value: '1018' },
      { label: '10306 — Γραμμή Ψυχοκοινωνικής Υποστήριξης', value: '10306' },
      { label: '112 — Ευρωπαϊκός Αριθμός Έκτακτης Ανάγκης', value: '112' },
    ],
  },

  hero: {
    eyebrow: 'ΑΝΩΝΥΜΗ ΨΥΧΟΛΟΓΙΚΗ ΥΠΟΣΤΗΡΙΞΗ',
    h1: 'Μίλησε σε ψυχολόγο. Χωρίς να μάθει κανείς ποιος είσαι.',
    subhead:
      'Συνεδρίες με αδειούχους ψυχολόγους, από το κινητό σου. Χωρίς κάμερα, με παραποιημένη φωνή αν το θέλεις, και χωρίς να δώσεις ποτέ το πραγματικό σου όνομα.',
    cta_primary: 'Ξεκίνα ανώνυμα',
    cta_secondary: 'Δες πρώτα τους ψυχολόγους',
    // REVISED 20/09/2026. The source JSON said "Ούτε email, ούτε τηλέφωνο."
    // Registration does require a verified email, so that claim was false and
    // could not ship. The anonymity axis survives intact because the promise
    // that actually matters is unchanged: the psychologist never learns who you
    // are. Owner to approve wording.
    cta_microcopy:
      'Η εγγραφή θέλει ένα ψευδώνυμο, ένα email και έναν κωδικό. Το email μένει σε εμάς — ο ψυχολόγος βλέπει μόνο το ψευδώνυμό σου.',
    image: {
      src: '/img/hero-window-light.jpg',
      alt: 'Απαλό πρωινό φως που περνά από μια κουρτίνα σε ήσυχο δωμάτιο',
    },
    trust_badges: [
      { icon: 'lock', text: 'Ψευδώνυμο, όχι όνομα' },
      { icon: 'shield-check', text: 'Μόνο αδειούχοι ψυχολόγοι' },
      { icon: 'mic-off', text: 'Χωρίς κάμερα, με αλλοιωμένη φωνή αν θες' },
    ],
  },

  barriers: {
    h2: 'Δεν είναι ότι δεν το έχεις σκεφτεί.',
    intro:
      'Το έχεις σκεφτεί. Ίσως έχεις ανοίξει και το Google. Και μετά σταμάτησες — όχι επειδή δεν το χρειάζεσαι, αλλά επειδή κάπου ήρθε μία από αυτές τις σκέψεις.',
    items: [
      {
        objection: '«Κι αν με δει κάποιος να μπαίνω;»',
        answer:
          'Δεν υπάρχει αίθουσα αναμονής. Δεν υπάρχει πόρτα. Η συνεδρία γίνεται από εκεί που ήδη είσαι.',
        icon: 'door-open',
      },
      {
        objection: '«Κι αν με αναγνωρίσει ο ίδιος ο ψυχολόγος;»',
        answer:
          'Δεν δίνεις όνομα. Η κάμερα είναι κλειστή. Και αν θέλεις, ακόμα και η φωνή σου ακούγεται διαφορετική.',
        icon: 'eye-off',
      },
      {
        objection: '«Δεν είμαι και τόσο χάλια για ψυχολόγο.»',
        answer:
          'Δεν χρειάζεται να είσαι. Οι περισσότεροι δεν έρχονται σε κρίση — έρχονται για κάτι που απλώς τους κουράζει πολύ καιρό.',
        icon: 'feather',
      },
      {
        objection: '«Κι αν μείνει κάπου γραμμένο;»',
        answer:
          'Δεν υπάρχει πραγματικό όνομα για να γραφτεί πουθενά. Οι συνεδρίες δεν καταγράφονται, και μπορείς να σβήσεις τον λογαριασμό σου όποτε θελήσεις.',
        icon: 'trash-2',
      },
    ],
  },

  how_it_works: {
    h2: 'Πώς λειτουργεί',
    subhead: 'Τρία βήματα. Κανένα δεν σε δεσμεύει στο επόμενο.',
    steps: [
      {
        number: '1',
        title: 'Φτιάξε ανώνυμο λογαριασμό',
        text: 'Διαλέγεις ένα ψευδώνυμο και έναν κωδικό. Τίποτα άλλο.',
        meta: '≈ 30 δευτερόλεπτα',
        image: { src: '/img/step-1-pebble.jpg', alt: 'Ένα βότσαλο σε ήρεμο νερό, κοντινή λήψη' },
      },
      {
        number: '2',
        title: 'Διάλεξε ψυχολόγο',
        text:
          'Δες ειδικότητα, γλώσσες, τιμή και ένα σύντομο βίντεο γνωριμίας. Χωρίς να μιλήσεις σε κανέναν πριν το αποφασίσεις.',
        meta: 'Με τον χρόνο σου',
        image: {
          src: '/img/step-2-desk.jpg',
          alt: 'Ήσυχο γραφείο με φυτό και απαλό φως, χωρίς ανθρώπους',
        },
      },
      {
        number: '3',
        title: 'Κάνε την πρώτη σου συνεδρία',
        text:
          'Κλείνεις ώρα και μπαίνεις από το κινητό σου. Χωρίς κάμερα. Με αλλοιωμένη φωνή, αν το επιλέξεις.',
        meta: 'Από όπου κι αν είσαι',
        image: { src: '/img/step-3-window.jpg', alt: 'Παράθυρο με θέα σε πράσινο, βραδινό φως' },
      },
    ],
  },

  anonymity: {
    h2: 'Τι σημαίνει «ανώνυμα» εδώ — συγκεκριμένα',
    subhead: 'Η λέξη χρησιμοποιείται εύκολα. Να τι ισχύει στην πράξη.',
    guarantees: [
      // REVISED 20/09/2026 — see hero.cta_microcopy.
      {
        text: 'Δεν δίνεις ποτέ το πραγματικό σου όνομα ούτε τηλέφωνο. Το email χρησιμεύει μόνο για να επιβεβαιωθεί και να προστατευτεί ο λογαριασμός σου — ο ψυχολόγος δεν το βλέπει.',
      },
      { text: 'Ο ψυχολόγος βλέπει μόνο το ψευδώνυμο που διάλεξες.' },
      { text: 'Η κάμερα είναι κλειστή εξ ορισμού. Ανοίγει μόνο αν την ανοίξεις εσύ.' },
      { text: 'Μπορείς να αλλοιώσεις τη φωνή σου. Ο ψυχολόγος ακούει τι λες, όχι πώς ακούγεσαι.' },
      { text: 'Οι συνεδρίες δεν καταγράφονται. Ποτέ, από κανέναν.' },
      { text: 'Διαγράφεις τον λογαριασμό σου, και ό,τι υπάρχει σε αυτόν, όποτε θέλεις.' },
    ],
    honest_limit_title: 'Και τι πρέπει να ξέρεις',
    // REVISED 20/09/2026. The original limit ("no email, so no password reset")
    // no longer applies. The replacement is the limit that now genuinely exists
    // and is the one the redesign brief itself flags in §4.2: holding an address
    // makes the service pseudonymous, not anonymous in the GDPR sense. Stating it
    // plainly is what keeps the other five guarantees credible.
    honest_limit_text:
      // Double-quoted because the copy contains an apostrophe ("Γι' αυτό").
      // The text is locked by the brief, so the quoting changes, never the words.
      "Κρατάμε τη διεύθυνση email σου, για να επιβεβαιώσουμε τον λογαριασμό και να μπορείς να τον ξανακερδίσεις αν χάσεις τον κωδικό. Σημαίνει ότι εμείς — όχι ο ψυχολόγος σου — μπορούμε τεχνικά να συνδέσουμε έναν λογαριασμό με ένα email. Γι' αυτό λέμε ότι είσαι ανώνυμος απέναντι στον θεραπευτή και σε κάθε άλλον χρήστη, όχι ότι δεν υπάρχει κανένα στοιχείο σου πουθενά.",
    honest_limit_text_2:
      'Αν πληρώσεις με κάρτα, η τράπεζά σου βλέπει μια χρέωση — όπως σε κάθε online αγορά. Τίποτα από όσα ειπώθηκαν στη συνεδρία δεν συνδέεται με αυτή τη χρέωση.',
    link_label: 'Διάβασε αναλυτικά την Πολιτική Απορρήτου',
    link_url: '/privacy',
  },

  psychologists: {
    h2: 'Οι ψυχολόγοι',
    subhead:
      'Κάθε προφίλ θεραπευτή ελέγχεται από την ομάδα μας πριν δημοσιευτεί. Δες ειδικότητα, γλώσσες, διάρκεια και τιμή συνεδρίας — χωρίς να κάνεις λογαριασμό.',
    filter_label: 'Επίλεξε τι σε απασχολεί',
    categories: [
      { key: 'all', label: 'Όλες οι κατηγορίες' },
      { key: 'anxiety', label: 'Άγχος' },
      { key: 'depression', label: 'Κατάθλιψη' },
      { key: 'relationships', label: 'Σχέσεις' },
      { key: 'grief', label: 'Πένθος' },
      { key: 'addiction', label: 'Εξαρτήσεις' },
      { key: 'trauma', label: 'Τραύμα & PTSD' },
      { key: 'self-esteem', label: 'Αυτοεκτίμηση' }, // slug must match prisma/seed.ts
      { key: 'stress', label: 'Διαχείριση στρες' },
      { key: 'family', label: 'Οικογένεια' },
      { key: 'lgbtq', label: 'Υποστήριξη ΛΟΑΤΚΙ+' },
    ],
    card_labels: {
      speaks: 'Μιλάει:',
      session_price: 'Συνεδρία',
      session_duration: 'Διάρκεια',
      watch_intro: 'Δες το βίντεο γνωριμίας',
      view_profile: 'Δες το προφίλ',
      book: 'Κλείσε συνεδρία',
    },
    type_labels: {
      psychologist: 'Αδειούχος ψυχολόγος',
      counselor: 'Σύμβουλος / Coaching',
      tab_psychologists: 'Ψυχολόγοι',
      tab_counselors: 'Σύμβουλοι & Coaching',
    },
    empty_state_title: 'Χτίζουμε αυτή τη στιγμή την πρώτη ομάδα θεραπευτών.',
    empty_state_text:
      'Η πλατφόρμα μόλις ξεκίνησε. Άφησε μας το ψευδώνυμό σου και θα σε ειδοποιήσουμε μόλις υπάρχει διαθέσιμος ψυχολόγος για αυτό που σε απασχολεί.',
    empty_state_cta: 'Ειδοποίησέ με',
  },

  pricing: {
    h2: 'Βλέπεις την τιμή πριν κλείσεις. Πάντα.',
    text:
      'Κάθε ψυχολόγος ορίζει τη δική του τιμή ανά συνεδρία και τη βλέπεις στο προφίλ του, πριν κάνεις οτιδήποτε. Δεν υπάρχει συνδρομή και δεν υπάρχει χρέωση που δεν έχεις δει πρώτα.',
    points: [
      {
        title: 'Καμία συνδρομή',
        text: 'Πληρώνεις τη συνεδρία που κλείνεις. Τίποτα δεν τρέχει στο παρασκήνιο.',
      },
      {
        title: 'Καμία δέσμευση',
        text: 'Δεν υπάρχει ελάχιστος αριθμός συνεδριών. Μία μπορεί να είναι και η μοναδική.',
      },
      {
        title: 'Αλλάζεις ελεύθερα',
        text: 'Αν δεν ταιριάξετε, διαλέγεις άλλον. Δεν χρειάζεται να εξηγήσεις σε κανέναν γιατί.',
      },
    ],
  },

  first_session: {
    h2: '«Και τι θα του πω;»',
    text:
      'Τίποτα που δεν θέλεις. Η πρώτη συνεδρία δεν είναι εξέταση και δεν χρειάζεται να έχεις ετοιμάσει κάτι. Συνήθως ξεκινάει με μια απλή ερώτηση — τι σε έφερε εδώ — και προχωράει με τον δικό σου ρυθμό.',
    highlight:
      'Μπορείς κάλλιστα να ξεκινήσεις με «δεν ξέρω από πού να αρχίσω». Είναι από τις πιο συνηθισμένες πρώτες προτάσεις που ακούει ένας ψυχολόγος.',
    image: {
      src: '/img/first-session-chair.jpg',
      alt: 'Άδεια πολυθρόνα δίπλα σε παράθυρο με απαλό φως',
    },
  },

  faq: {
    h2: 'Συχνές ερωτήσεις',
    subhead: 'Ό,τι ρωτούν οι περισσότεροι πριν κάνουν το πρώτο βήμα.',
    items: [
      {
        q: 'Είναι αληθινοί ψυχολόγοι ή κάποιο chatbot;',
        a: 'Αληθινοί άνθρωποι με άδεια ασκήσεως επαγγέλματος. Η πλατφόρμα δεν παρέχει αυτοματοποιημένες απαντήσεις ή τεχνητή νοημοσύνη στη θέση θεραπευτή.',
      },
      {
        q: 'Πόσο ανώνυμο είναι πραγματικά;',
        // REVISED 20/09/2026 — see hero.cta_microcopy.
        a: 'Απέναντι στον ψυχολόγο και σε κάθε άλλον χρήστη, πλήρως: βλέπουν μόνο το ψευδώνυμό σου. Δεν δίνεις όνομα ούτε τηλέφωνο. Ζητάμε ένα email, αποκλειστικά για να επιβεβαιώσουμε τον λογαριασμό και να τον προστατέψουμε — δεν κοινοποιείται πουθενά. Η κάμερα είναι κλειστή εξ ορισμού και ανοίγει μόνο αν την ανοίξεις εσύ, ενώ μπορείς και να αλλοιώσεις τη φωνή σου. Το μόνο σημείο που δεν ελέγχουμε είναι η τραπεζική χρέωση, αν πληρώσεις με κάρτα.',
      },
      {
        q: 'Μπορεί να με αναγνωρίσει ο ψυχολόγος από τη φωνή μου;',
        a: 'Αν ενεργοποιήσεις την παραποίηση φωνής, η φωνή σου ακούγεται διαφορετική. Ο ψυχολόγος ακούει καθαρά τι λες, χωρίς να ακούει τη φυσική σου χροιά.',
      },
      {
        q: 'Πόσο κοστίζει μια συνεδρία;',
        a: 'Κάθε ψυχολόγος ορίζει τη δική του τιμή. Τη βλέπεις στο προφίλ του πριν κλείσεις ραντεβού. Δεν υπάρχει συνδρομή και δεν υπάρχει κρυφή χρέωση.',
      },
      {
        q: 'Τι γίνεται αν δεν μου ταιριάξει ο ψυχολόγος;',
        a: 'Διαλέγεις άλλον. Δεν χρειάζεται να δικαιολογηθείς και δεν χάνεις τίποτα. Το να μη «κουμπώσεις» με τον πρώτο είναι απολύτως συνηθισμένο.',
      },
      {
        q: 'Πρέπει να είμαι σε κάποια κρίση για να ξεκινήσω;',
        a: 'Όχι. Οι περισσότεροι άνθρωποι ζητούν υποστήριξη για κάτι που τους κουράζει καιρό — στη δουλειά, σε μια σχέση, στον ύπνο τους — όχι επειδή κάτι κατέρρευσε.',
      },
      {
        q: 'Πόσο διαρκεί μια συνεδρία;',
        a: 'Τη διάρκεια την ορίζει ο κάθε θεραπευτής, μαζί με την τιμή του. Και τα δύο φαίνονται στο προφίλ του πριν κλείσεις ραντεβού.',
      },
      {
        q: 'Καταγράφονται οι συνεδρίες;',
        a: 'Όχι. Καμία συνεδρία δεν ηχογραφείται και δεν βιντεοσκοπείται — ούτε από εμάς, ούτε από τον θεραπευτή μέσα από την πλατφόρμα.',
      },
      {
        q: 'Τι γίνεται αν ξεχάσω τον κωδικό μου;',
        // REVISED 20/09/2026 — with a verified email on file, recovery exists.
        a: 'Σου στέλνουμε σύνδεσμο επαναφοράς στο email που δήλωσες. Το email δεν εμφανίζεται πουθενά στην πλατφόρμα και δεν το βλέπει ο ψυχολόγος σου.',
      },
      {
        q: 'Μπορώ να διαγράψω τον λογαριασμό μου;',
        a: 'Ναι, όποτε θέλεις, μέσα από τις ρυθμίσεις του λογαριασμού σου.',
      },
      {
        q: 'Τι γίνεται αν βρίσκομαι σε επείγουσα κατάσταση;',
        a: 'Η πλατφόρμα δεν αντικαθιστά επείγουσα φροντίδα και δεν λειτουργεί 24/7 ως γραμμή κρίσης. Αν κινδυνεύεις τώρα, κάλεσε 1018 (Γραμμή Παρέμβασης για την Αυτοκτονία), 10306 ή 112.',
      },
    ],
  },

  therapist_cta: {
    h2: 'Είσαι αδειούχος ψυχολόγος;',
    text:
      'Η πλατφόρμα ξεκινά τώρα και χτίζουμε την πρώτη ομάδα θεραπευτών. Ορίζεις τη δική σου τιμή, το δικό σου ωράριο και δουλεύεις από όπου είσαι. Η εγγραφή είναι δωρεάν.',
    cta: 'Δωρεάν εγγραφή θεραπευτή',
    cta_url: 'https://therapists.anonymoushelp.gr/register',
  },

  final_cta: {
    h2: "Το πρώτο βήμα είναι το πιο δύσκολο. Κάν' το ανώνυμα.",
    text:
      'Δεν χρειάζεται να πεις ούτε το όνομά σου για να ζητήσεις βοήθεια. Η εγγραφή παίρνει λιγότερο από ένα λεπτό και δεν σε δεσμεύει σε τίποτα.',
    cta_primary: 'Ξεκίνα ανώνυμα',
    cta_microcopy: 'Δωρεάν εγγραφή · Μόνο ψευδώνυμο · Καμία συνδρομή',
    image: { src: '/img/final-path.jpg', alt: 'Μονοπάτι που ανοίγει μέσα σε απαλό πρωινό φως' },
  },

  footer: {
    about_title: 'Anonymous Help',
    about_text: 'Ανώνυμη ψυχολογική υποστήριξη με αδειούχους ψυχολόγους, από το κινητό σου.',
    contact_title: 'Επικοινωνία',
    phone_gr_label: 'Ελλάδα',
    phone_gr: '+30 694 825 7557',
    phone_cy_label: 'Κύπρος',
    phone_cy: '+357 99 140 329',
    email: 'info@anonymoushelp.gr',
    social: { facebook_url: '', instagram_url: '' },
    disclaimer_title: 'Σημαντικό',
    disclaimer:
      'Η πλατφόρμα δεν αντικαθιστά επείγουσα φροντίδα. Αν βρίσκεσαι σε κρίση, κάλεσε 1018 (Γραμμή Παρέμβασης για την Αυτοκτονία), 10306 ή 112.',
    links: [
      { label: 'Όροι Χρήσης', url: '/terms' },
      { label: 'Πολιτική Απορρήτου', url: '/privacy' },
    ],
    copyright: '© 2026 Anonymous Help',
  },

  seo: {
    title: 'Anonymous Help — Ανώνυμες συνεδρίες με ψυχολόγο',
    meta_description:
      'Μίλησε σε αδειούχο ψυχολόγο χωρίς να δώσεις το όνομά σου. Χωρίς κάμερα, με παραποιημένη φωνή αν το θέλεις. Δωρεάν εγγραφή με ψευδώνυμο.',
    og_title: 'Μίλησε σε ψυχολόγο. Χωρίς να μάθει κανείς ποιος είσαι.',
    og_description: 'Ανώνυμες συνεδρίες με αδειούχους ψυχολόγους, από το κινητό σου.',
    og_image: '/img/og-anonymoushelp.jpg',
  },
};

/** `en` is intentionally empty for now — falls back to `el` per key (brief §2 of the task list). */
export const LANDING_CONTENT: Record<string, Partial<LandingContent>> = {
  el: LANDING_CONTENT_EL,
  en: {},
};

/**
 * Merge a CMS block over the shipped default, one section at a time.
 * Any parse failure or malformed body falls back to the default silently: a
 * marketing page must never render an error, and a bad draft must never blank it.
 */
export function readSection<K extends LandingSection>(
  section: K,
  raw: string | undefined,
  locale = 'el',
): LandingContent[K] {
  const base = ((LANDING_CONTENT[locale] as LandingContent | undefined)?.[section] ??
    LANDING_CONTENT_EL[section]) as LandingContent[K];
  if (!raw) return base;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return base;
    const merged = { ...(base as object), ...(parsed as object) } as LandingContent[K];
    // The crisis bar is never disableable, whatever a stored draft claims (brief §5 rule 1).
    if (section === 'crisis_bar') {
      (merged as CrisisBarContent).enabled_locked = true;
      const lines = (merged as CrisisBarContent).lines;
      if (!Array.isArray(lines) || lines.length === 0) {
        (merged as CrisisBarContent).lines = LANDING_CONTENT_EL.crisis_bar.lines;
      }
    }
    return merged;
  } catch {
    return base;
  }
}
